/**
 * @ddd/analytics — consent-aware GA4 helper for the DDD fleet.
 *
 * One small surface every app shares:
 *
 *   initAnalytics({ measurementId: 'G-XXXXXXXXXX', appId: 'profound-philosophy' })
 *   setAnalyticsConsent(true)          // after the user says yes
 *   trackModuleComplete('logic-and-reasoning')
 *
 * Design constraints (do not weaken these):
 *  - No network request of any kind until consent is granted. The gtag.js
 *    loader itself is only injected post-consent, so apps whose privacy pages
 *    promise "nothing leaves your device unless you opt in" stay honest —
 *    Consent Mode's cookieless pings still hit Google, so default-denied
 *    Consent Mode alone is not enough.
 *  - Event parameters carry identifiers and enums only, never user content.
 *    Everything is passed through sanitiseParams(), which drops free text.
 *  - Advertising signals are permanently denied; this fleet does not run ads.
 *  - Global Privacy Control is a standing "no": the loader never injects and
 *    the notice never asks while the browser sends it.
 *  - Without a measurementId every call is a silent no-op, so the helper is
 *    safe to wire into an app before its GA4 stream exists.
 *  - GENERAL-tier apps only. Sensitive and teen apps (packages/analytics/
 *    tiers.json, docs/ANALYTICS_STANDARD.md) must not import this at all —
 *    packages/analytics/scripts/check-tiers.mjs fails them if they do.
 *
 * SPA page_views: GA4 enhanced measurement (on by default per web stream)
 * tracks history changes, so React Router navigation needs no code here.
 *
 * Hash-routed apps: whether GA4's page_location keeps the "#" fragment hasn't
 * been checked on a live stream (2026-10-17). The fleet probe of 2026-09-26
 * lists the automatic page_view as leaking only tokens in the query string, but
 * it didn't cover hash routes or history page views. Don't rely on it either way: an app that puts
 * anything private in a hash route lifts it out of the address before
 * initAnalytics (apps/storycrew/src/lib/linkSecrets.js), as every app does for
 * hub tokens (scripts/security/url-secrets).
 */

const CONSENT_STORAGE_KEY = 'ddd_analytics_consent';

const state = {
  measurementId: null,
  appId: null,
  loaderInjected: false,
  debug: false,
};

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const isLocalhost = () =>
  isBrowser() &&
  /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$/.test(window.location.hostname);

/**
 * True when the browser sends Global Privacy Control (Firefox, Brave, DuckDuckGo
 * and extensions). The fleet treats it as a standing "no" — honoured as an
 * opt-out in California and good practice everywhere else — so the loader never
 * injects and the consent notice never asks. See docs/ANALYTICS_STANDARD.md.
 */
export function isGlobalPrivacyControlOn() {
  return isBrowser() && typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}

/** Read the stored consent decision: true, false, or null (never asked). */
export function getStoredConsent() {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw === 'granted') return true;
    if (raw === 'denied') return false;
  } catch {
    /* private browsing / storage disabled — treat as never asked */
  }
  return null;
}

export function hasAnalyticsConsent() {
  return getStoredConsent() === true;
}

// Must be a classic function pushing the `arguments` object itself — NOT a
// rest-param array. gtag.js identifies its commands by [object Arguments] and
// silently drops plain arrays: the loader still loads and
// window.google_tag_manager still appears, but no config runs, no _ga cookie
// is set and nothing is ever sent to /g/collect. Verified against
// G-VEP2FEQQL4 before changing this.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function injectLoader() {
  if (state.loaderInjected || !state.measurementId || !isBrowser()) return;
  if (isGlobalPrivacyControlOn()) return; // a browser-level "no" outranks a stored or in-app yes
  if (isLocalhost() && !state.debug) return; // keep dev traffic out of production data

  state.loaderInjected = true;

  gtag('js', new Date());
  // Advertising signals stay denied forever; analytics is granted because the
  // loader is only ever injected after explicit consent.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });
  gtag('config', state.measurementId, {
    app_id: state.appId || undefined,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(state.measurementId)}`;
  document.head.appendChild(script);

  // Expose the classic function form so existing inline gtag('event', …)
  // boilerplate in app shells keeps working once analytics is live.
  if (typeof window.gtag !== 'function') {
    window.gtag = gtag;
  }
}

/**
 * Initialise analytics for an app. Call once at startup.
 * Injects the GA4 loader only if the user has previously granted consent;
 * otherwise nothing loads until setAnalyticsConsent(true).
 */
export function initAnalytics({ measurementId, appId, debug = false } = {}) {
  if (!isBrowser() || !measurementId) return;
  state.measurementId = measurementId;
  state.appId = appId || null;
  state.debug = Boolean(debug);

  if (getStoredConsent() === true) {
    injectLoader();
  }
}

/**
 * Record the user's consent decision and act on it.
 * Granting injects the loader (first grant) — denying stops future loads and
 * asks GA to drop analytics storage for this session.
 */
export function setAnalyticsConsent(granted) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied');
  } catch {
    /* storage unavailable — the in-page decision below still applies */
  }

  if (granted) {
    injectLoader();
  } else if (state.loaderInjected) {
    gtag('consent', 'update', { analytics_storage: 'denied' });
  }
}

const SAFE_STRING = /^[a-z0-9_./:-]{1,100}$/i;

/**
 * Keep only parameters that cannot smuggle user content: short identifier-like
 * strings, finite numbers, booleans. Anything else is dropped.
 */
function sanitiseParams(params = {}) {
  const clean = {};
  for (const [key, value] of Object.entries(params)) {
    if (!/^[a-z][a-z0-9_]{0,39}$/i.test(key)) continue;
    if (typeof value === 'boolean') clean[key] = value;
    else if (typeof value === 'number' && Number.isFinite(value)) clean[key] = value;
    else if (typeof value === 'string' && SAFE_STRING.test(value)) clean[key] = value;
  }
  return clean;
}

/**
 * Low-level event call. Prefer the named helpers below — they keep event
 * naming consistent across the fleet.
 */
export function trackEvent(name, params = {}) {
  if (!isBrowser() || !state.loaderInjected) return;
  if (!/^[a-z][a-z0-9_]{0,39}$/i.test(name)) return;
  gtag('event', name, sanitiseParams(params));
}

// ---------------------------------------------------------------------------
// Fleet-standard events. Identifiers only — module slugs, tool ids, enums.
// ---------------------------------------------------------------------------

export function trackModuleStart(moduleId) {
  trackEvent('module_start', { module_id: moduleId });
}

export function trackModuleComplete(moduleId) {
  trackEvent('module_complete', { module_id: moduleId });
}

export function trackAssessmentComplete(profileKey) {
  trackEvent('assessment_complete', { profile_key: profileKey });
}

export function trackToolUsed(toolId) {
  trackEvent('tool_used', { tool_id: toolId });
}

export function trackToolComplete(toolId) {
  trackEvent('tool_complete', { tool_id: toolId });
}

/** granted: boolean — the user's companion (AI coach) consent decision. */
export function trackCompanionConsent(granted) {
  trackEvent('companion_consent', { granted: Boolean(granted) });
}

/** method: 'hub' | 'email' | 'guest' — never an address or name. */
export function trackSignIn(method) {
  trackEvent('login', { method });
}

export function trackShare(surface) {
  trackEvent('share', { surface });
}

// First-visit consent notice (React). Lives in its own module so this file
// stays importable outside a JSX-aware bundler; the re-export is what apps
// consume via the '@ddd/analytics' alias.
export { AnalyticsConsentNotice } from './ConsentNotice.jsx';
